import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import client, { url } from '../../lib/api/client';

const Container = styled.div`
  padding: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background-color: #fff;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  margin: 0 0 20px 0;
  color: #333;
  font-size: 22px;
  font-weight: 700;
  padding-bottom: 15px;
  border-bottom: 2px solid #e0e0e0;
`;

const InfoBox = styled.div`
  background-color: #f5f5f5;
  border-left: 4px solid #1976d2;
  padding: 16px 20px;
  margin-bottom: 24px;
  border-radius: 4px;
`;

const InfoTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin-bottom: 12px;
`;

const InfoList = styled.ul`
  margin: 0;
  padding-left: 20px;
  list-style: none;
`;

const InfoItem = styled.li`
  font-size: 14px;
  color: #555;
  line-height: 1.8;
  position: relative;
  padding-left: 16px;

  &:before {
    content: '•';
    position: absolute;
    left: 0;
    color: #1976d2;
    font-weight: bold;
  }
`;

const NoticeCard = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px;
  border: 2px solid ${props => props.inactive ? '#eee' : '#e3f2fd'};
  border-radius: 10px;
  margin-bottom: 12px;
  background-color: ${props => props.inactive ? '#fafafa' : '#fff'};
  opacity: ${props => props.inactive ? 0.65 : 1};
`;

const OrderColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 40px;
  flex-shrink: 0;
`;

const OrderIndex = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1976d2;
  margin-bottom: 2px;
`;

const OrderButton = styled.button`
  width: 26px;
  height: 22px;
  border: 1px solid #e0e0e0;
  background: #fff;
  border-radius: 4px;
  font-size: 11px;
  color: #666;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.35 : 1};
  padding: 0;

  &:hover {
    background: ${props => props.disabled ? '#fff' : '#f0f0f0'};
  }
`;

const FieldColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 8px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: ${props => props.flex || 1};
  min-width: 0;
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #888;
`;

const FieldInput = styled.input`
  padding: 8px 10px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #1976d2;
  }
`;

const FieldTextarea = styled.textarea`
  padding: 8px 10px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  min-height: 56px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #1976d2;
  }
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  width: 110px;
  flex-shrink: 0;
`;

const ActiveLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #555;
  cursor: pointer;
`;

const DeleteButton = styled.button`
  padding: 6px 12px;
  border: none;
  background: #ffebee;
  color: #c62828;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #ffcdd2;
  }
`;

const AddButton = styled.button`
  padding: 10px 18px;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 4px;

  &:hover {
    background-color: #1565c0;
  }
`;

const ApplyButton = styled.button`
  padding: 12px 24px;
  background-color: ${props => props.disabled ? '#ccc' : '#1976d2'};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin-top: 20px;

  &:hover {
    background-color: ${props => props.disabled ? '#ccc' : '#1565c0'};
  }
`;

const EmptyText = styled.div`
  font-size: 14px;
  color: #999;
  padding: 24px 0;
  text-align: center;
`;

/* TV 화면 미리보기 — 실제 표시 레이아웃과 같은 구성 */
const PreviewWrapper = styled.div`
  margin-top: 8px;
  padding: 20px;
  background: linear-gradient(180deg, #0d0d0d 0%, #1a1a2e 100%);
  border-radius: 10px;
`;

const PreviewLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 10px;
`;

const PreviewCard = styled.div`
  background: rgba(0, 0, 0, 0.55);
  border-radius: 14px;
  padding: 20px 24px;
  text-align: center;
`;

const PreviewBadge = styled.div`
  font-size: 12px;
  color: #7ee2a8;
  margin-bottom: 10px;
`;

const PreviewTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  line-height: 1.4;
`;

const PreviewBody = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.6;
  margin-top: 8px;
`;

const PreviewTags = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  margin-top: 12px;
`;

const PreviewDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 5px;
  margin-top: 12px;
`;

const PreviewDot = styled.span`
  width: ${props => props.active ? '14px' : '5px'};
  height: 5px;
  border-radius: 3px;
  background: ${props => props.active ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.25)'};
`;

const MAX_NOTICE = 10;

const RunningTrackNoticeComponent = () => {
  const [notices, setNotices] = useState([]);
  const [originalNotices, setOriginalNotices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  const {
    club
  } = useSelector(({ club }) => ({
    club: club.defaultClub,
  }));

  // 공지 목록 로드
  const loadNotice = async () => {
    if (!club) return;

    try {
      setIsLoading(true);
      const res = await client.get(url + '/api/runningtrack/notice/' + club.clubID);
      const list = res.data.notices || [];
      setNotices(list);
      setOriginalNotices(JSON.parse(JSON.stringify(list)));
      setPreviewIndex(0);
    } catch (error) {
      console.error('Failed to load notice:', error);
      alert('공지사항을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadNotice();
  }, [club]);

  const isModified = JSON.stringify(notices) !== JSON.stringify(originalNotices);

  // 공지 추가 — 서버가 ID와 노출 순서를 정하므로 등록 후 다시 읽어온다.
  const handleAdd = async () => {
    if (!club) return;
    if (notices.length >= MAX_NOTICE) {
      alert('공지는 최대 ' + MAX_NOTICE + '개까지 등록할 수 있습니다.');
      return;
    }
    if (isModified) {
      alert('저장하지 않은 변경사항이 있습니다. 먼저 적용하기를 눌러주세요.');
      return;
    }

    try {
      setIsLoading(true);
      await client.post(url + '/api/runningtrack/notice/' + club.clubID, {
        title: '새 공지',
        body: '',
        period: '',
        place: '',
        active: 1,
      });
      await loadNotice();
    } catch (error) {
      console.error('Failed to add notice:', error);
      alert('공지 추가에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 공지 삭제
  const handleDelete = async (notice) => {
    if (!window.confirm('이 공지를 삭제하시겠습니까?')) return;

    try {
      setIsLoading(true);
      await client.delete(url + '/api/runningtrack/notice/' + notice.runningTrackNoticeID);
      await loadNotice();
    } catch (error) {
      console.error('Failed to delete notice:', error);
      alert('공지 삭제에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (index, field, value) => {
    const next = JSON.parse(JSON.stringify(notices));
    next[index][field] = value;
    setNotices(next);
  };

  // 노출 순서 이동 (적용하기를 눌러야 서버에 반영된다)
  const handleMove = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= notices.length) return;
    const next = notices.slice();
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    setNotices(next);
    setPreviewIndex(target);
  };

  // 변경된 공지 내용 + 순서를 저장한다.
  const handleSave = async () => {
    if (!club || !isModified) return;

    for (let i = 0; i < notices.length; i++) {
      if (!notices[i].title || notices[i].title.trim() === '') {
        alert((i + 1) + '번 공지의 제목을 입력해주세요.');
        return;
      }
    }

    try {
      setIsLoading(true);

      // 내용이 바뀐 공지만 갱신
      for (let i = 0; i < notices.length; i++) {
        const notice = notices[i];
        const origin = originalNotices.find(function(o) {
          return o.runningTrackNoticeID === notice.runningTrackNoticeID;
        });
        const changed = !origin
          || origin.title !== notice.title
          || origin.body !== notice.body
          || origin.period !== notice.period
          || origin.place !== notice.place
          || Number(origin.active) !== Number(notice.active);

        if (changed) {
          await client.patch(url + '/api/runningtrack/notice/' + notice.runningTrackNoticeID, {
            title: notice.title,
            body: notice.body || '',
            period: notice.period || '',
            place: notice.place || '',
            active: Number(notice.active) === 0 ? 0 : 1,
          });
        }
      }

      // 순서가 바뀐 경우에만 순서 저장
      const orderChanged = notices.map(function(n) { return n.runningTrackNoticeID; }).join(',')
        !== originalNotices.map(function(n) { return n.runningTrackNoticeID; }).join(',');
      if (orderChanged) {
        await client.post(url + '/api/runningtrack/notice/' + club.clubID + '/order', {
          noticeIDs: notices.map(function(n) { return n.runningTrackNoticeID; }),
        });
      }

      await loadNotice();
      alert('공지사항이 저장되었습니다.');
    } catch (error) {
      console.error('Failed to save notice:', error);
      alert('공지사항 저장에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 미리보기는 노출 ON 인 공지만 대상으로 하며, 순서를 바꿔 인덱스가 밀리면 첫 장으로 돌린다.
  const activeNotices = notices.filter(function(n) { return Number(n.active) !== 0; });
  const safePreviewIndex = activeNotices[previewIndex] ? previewIndex : 0;
  const previewTarget = activeNotices[safePreviewIndex];

  return (
    <Container>
      <Title>TV 공지사항</Title>

      <InfoBox>
        <InfoTitle>공지사항 설정 안내</InfoTitle>
        <InfoList>
          <InfoItem>등록된 공지는 TV 화면 중앙에 <strong>순서대로 번갈아 노출</strong>됩니다</InfoItem>
          <InfoItem>노출을 꺼두면 삭제하지 않고 화면에서만 숨길 수 있습니다</InfoItem>
          <InfoItem>공지는 최대 {MAX_NOTICE}개까지 등록할 수 있습니다</InfoItem>
          <InfoItem>제목 50자, 내용 300자, 기간/장소 각 50자까지 입력됩니다</InfoItem>
        </InfoList>
      </InfoBox>

      {notices.length === 0 && !isLoading && (
        <EmptyText>등록된 공지가 없습니다</EmptyText>
      )}

      {notices.map(function(notice, index) {
        const inactive = Number(notice.active) === 0;
        return (
          <NoticeCard key={notice.runningTrackNoticeID} inactive={inactive}>
            <OrderColumn>
              <OrderIndex>{index + 1}</OrderIndex>
              <OrderButton
                onClick={function() { handleMove(index, -1); }}
                disabled={index === 0}
              >▲</OrderButton>
              <OrderButton
                onClick={function() { handleMove(index, 1); }}
                disabled={index === notices.length - 1}
              >▼</OrderButton>
            </OrderColumn>

            <FieldColumn>
              <FieldGroup>
                <FieldLabel>제목</FieldLabel>
                <FieldInput
                  type="text"
                  value={notice.title || ''}
                  maxLength={50}
                  placeholder="예) 9월 2일 화요일 정기 점검이 있습니다"
                  onChange={function(e) { handleChange(index, 'title', e.target.value); }}
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>내용</FieldLabel>
                <FieldTextarea
                  value={notice.body || ''}
                  maxLength={300}
                  placeholder="예) 오전 9시부터 12시까지 러닝존을 사용할 수 없습니다."
                  onChange={function(e) { handleChange(index, 'body', e.target.value); }}
                />
              </FieldGroup>

              <FieldRow>
                <FieldGroup>
                  <FieldLabel>기간</FieldLabel>
                  <FieldInput
                    type="text"
                    value={notice.period || ''}
                    maxLength={50}
                    placeholder="예) 09:00 - 12:00"
                    onChange={function(e) { handleChange(index, 'period', e.target.value); }}
                  />
                </FieldGroup>
                <FieldGroup>
                  <FieldLabel>장소</FieldLabel>
                  <FieldInput
                    type="text"
                    value={notice.place || ''}
                    maxLength={50}
                    placeholder="예) 러닝존 전체"
                    onChange={function(e) { handleChange(index, 'place', e.target.value); }}
                  />
                </FieldGroup>
              </FieldRow>
            </FieldColumn>

            <SideColumn>
              <ActiveLabel>
                <input
                  type="checkbox"
                  checked={!inactive}
                  onChange={function(e) { handleChange(index, 'active', e.target.checked ? 1 : 0); }}
                />
                노출
              </ActiveLabel>
              <DeleteButton onClick={function() { handleDelete(notice); }}>삭제</DeleteButton>
            </SideColumn>
          </NoticeCard>
        );
      })}

      <div>
        <AddButton onClick={handleAdd}>공지 추가</AddButton>
      </div>

      {previewTarget && (
        <PreviewWrapper>
          <PreviewLabel>TV 화면 미리보기</PreviewLabel>
          <PreviewCard>
            <PreviewBadge>● 공지</PreviewBadge>
            <PreviewTitle>{previewTarget.title || '제목을 입력해주세요'}</PreviewTitle>
            {previewTarget.body && <PreviewBody>{previewTarget.body}</PreviewBody>}
            {(previewTarget.period || previewTarget.place) && (
              <PreviewTags>
                {previewTarget.period}
                {previewTarget.period && previewTarget.place ? '   |   ' : ''}
                {previewTarget.place}
              </PreviewTags>
            )}
            <PreviewDots>
              {activeNotices.map(function(n, i) {
                return (
                  <PreviewDot
                    key={n.runningTrackNoticeID}
                    active={i === safePreviewIndex}
                    onClick={function() { setPreviewIndex(i); }}
                  />
                );
              })}
            </PreviewDots>
          </PreviewCard>
        </PreviewWrapper>
      )}

      <ApplyButton
        onClick={handleSave}
        disabled={!isModified || isLoading}
      >
        {isLoading ? '저장 중...' : '적용하기'}
      </ApplyButton>
    </Container>
  );
};

export default RunningTrackNoticeComponent;
